import { useCallback, useEffect, useState } from "react";
import { rouletteItemBgType, rouletteItemFrameType, rouletteItemTextAlignment, rouletteItemTextTransformation } from "../../RouletteConstants";
import { Box, Button, Card, CardBody, CardFooter, CardHeader, Form, FormField, Heading, Markdown, Select, Tab, Tabs, Text, TextInput } from "grommet";
import { Close } from "grommet-icons";
import RouletteItemDesignTab from "../tab/RouletteItemDesignTab";
import { useTranslation } from "react-i18next";
import { useUpdateRouletteCategoryMutation } from "../../../../api/rouletteWidgetApi";
import { InfoSection } from "../../../layout/InfoSection";
import { RouletteTipsTranslation } from "../../../../md-translation/RouletteTipsTranslation";
import { RouletteItemThemesTab } from "../tab/RouletteItemThemesTab";
import { mergeProps } from "../../../../utils/mergeUtils";

const rarityOptions = [
    {
        chance: 500,
        color: '#fdfefe',
        labelKey: 'rarityCommon'
    },
    {
        chance: 250,
        color: '#2fde78',
        labelKey: 'rarityUncommon'
    },
    {
        chance: 160,
        color: '#385ef5',
        labelKey: 'rarityRare'
    },
    {
        chance: 70,
        color: '#a14ac7',
        labelKey: 'rarityEpic'
    },
    {
        chance: 15,
        color: '#f1c40f',
        labelKey: 'rarityLegendary'
    },
    {
        chance: 7,
        color: '#D35400',
        labelKey: 'rarityMythic'
    },
    {
        chance: 1,
        color: '#c70404',
        labelKey: 'rarityRelic'
    }
]

const categoryValidationRules = (t) => [
    {
        regexp: /^.{1,128}$/,
        message: t('maxCharacters', { max: 128 }),
        status: 'error',
    }
]

const rewardNamesValidationRules = (t) => [
    {
        regexp: /^.{1,256}$/,
        message: t('maxCharacters', { max: 256 }),
        status: 'error',
    }
]

const RouletteItemSettingsForm = ({ values, rouletteBackgroundStyle, submitBusy, submitSuccess, onClose, onSubmit, deleteBusy, deleteSuccess, onDelete }) => {

    const { t, i18n, ready } = useTranslation('roulette', { useSuspense: false });

    const [rewardForm, setRewardForm] = useState(values ? values : {
        categoryName: '',
        rewardNames: '',
        rarity: 500,
        backgroundType: rouletteItemBgType.COLOR,
        backgroundColor1: "#474747",
        backgroundColor2: "#2f2f2f",
        colorGradientAngle: 306,
        backgroundRadius: 6,
        backgroundSize: 150,
        backgroundShadowColor: "black",
        backgroundShadowHorizontalOffset: 0,
        backgroundShadowVerticalOffset: 0,
        backgroundShadowBlur: 0,
        backgroundImage: null,
        backgroundImageHue: 0,
        backgroundImageSaturation: 100,
        frameType: rouletteItemFrameType.OUTLINE,
        frameColor1: "#a00259",
        frameColor2: "#a00259",
        frameColorGradientAngle: 0,
        frameImage: null,
        frameImageHue: 0,
        frameImageSaturation: 100,
        frameRadius: 12,
        frameOutlineWidth: 5,
        frameSize: 160,
        frameShadowColor: "black",
        frameShadowHorizontalOffset: 2,
        frameShadowVerticalOffset: 2,
        frameShadowBlur: 2,
        font: {
            id: 81,
            name: "Fira Sans",
            type: "font",
            url: "https://fonts.googleapis.com/css2?family=Fira+Sans:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap",
            category: {
                code: "roulette_category_font",
                name: null
            }
        },
        textColor: "#FFFFFF",
        textStyle: [{ value: "bold", selected: true }, { value: "italic", selected: false }, { value: "underline", selected: false }],
        // textStyle: "bold",
        textTransformation: rouletteItemTextTransformation.NONE,
        textAlignment: rouletteItemTextAlignment.CENTER,
        fontSize: 16,
        lineHeight: 1.5,
        letterSpacing: 3,
        wordSpacing: 0,
        textShadowColor: "black",
        textShadowHorizontalOffset: 2,
        textShadowVerticalOffset: 2,
        textShadowBlur: 2,
        fontOutlineWidth: 0,
        textOutlineColor: "black",
        textOutlineBackgroundSize: 0,
        textOutlineBackgroundColor: "#ffffff",
        fontBlockWidth: 130,
        fontPositionX: 0,
        fontPositionY: 0
    });

    const handleFormChange = (value) => {
        if (value.colorGradientAngle === null || value.colorGradientAngle === undefined || value.colorGradientAngle === '') {
            value.colorGradientAngle = 0;
        } else if (Number(value.colorGradientAngle) < 0) {
            value.colorGradientAngle = 0;
        } else if (Number(value.colorGradientAngle) > 360) {
            value.colorGradientAngle = 360;
        }
        setRewardForm(value);
    }

    const handleThemeApply = useCallback((theme) => {
        const newRewardForm = mergeProps(rewardForm, theme);

        delete newRewardForm.component;
        delete newRewardForm.category;

        newRewardForm.id = rewardForm.id;

        setRewardForm(newRewardForm);
    }, [rewardForm])

    return (
        <Card>
            <Form value={rewardForm} onChange={handleFormChange} onSubmit={onSubmit}>
                <CardHeader direction="row" justify="between" pad={{ top: 'medium', left: 'medium', right: 'medium', bottom: 'none' }}>
                    <Heading level={3} margin={'none'}>{t('configureCategoryHeader')}</Heading>
                    <Button icon={<Close />} onClick={onClose} />
                </CardHeader>
                <CardBody gap="small" direction="row" justify="between" align="center">
                    <Box flex>
                        <Tabs>
                            <Tab title={t('categoryTabTitle')}>
                                <Box direction="row" gap="medium" fill>
                                    <Box fill>
                                        <FormField label={t('categoryName')} htmlFor="categoryName" name="categoryName" required={{indicator: true}} validate={categoryValidationRules(t)}>
                                            <TextInput id="categoryName" name="categoryName"/>
                                        </FormField>
                                        <FormField label={t('rewardNames')} htmlFor="rewardNames" name="rewardNames" required={{indicator: true}} info={t('rewardNamesInfo')} validate={rewardNamesValidationRules(t)}>
                                            <TextInput id="rewardNames" name="rewardNames"/>
                                        </FormField>
                                        <FormField label={t('rarity')} htmlFor="rarity" name="rarity">
                                            <Select 
                                                id="rarity"
                                                name="rarity"
                                                options={rarityOptions}
                                                labelKey={(option) => <Box pad="5px"><Text color={option.color}>{t(option.labelKey)}</Text></Box>}
                                                valueKey={{ key: "chance", reduce: true }}
                                            />
                                        </FormField>
                                    </Box>
                                    <Box gap="medium" fill>
                                        <InfoSection>
                                            <Box>
                                                <Markdown components={{ p: { props: { style: { maxWidth: '100%' } } } }}>{RouletteTipsTranslation.rewardNames[i18n.language]}</Markdown>
                                            </Box>
                                        </InfoSection>
                                        <InfoSection>
                                            <Box>
                                                <Markdown components={{ p: { props: { style: { maxWidth: '100%' } } } }}>{RouletteTipsTranslation.rarity[i18n.language]}</Markdown>
                                            </Box>
                                        </InfoSection>
                                    </Box>
                                </Box>
                            </Tab>
                            <Tab title={t('designTabTitle')}>
                                <RouletteItemDesignTab designValues={rewardForm} backgroudDesignValues={rouletteBackgroundStyle} />
                            </Tab>
                            <Tab title={t('themesTabTitle')}>
                                <RouletteItemThemesTab backgroudDesignValues={rouletteBackgroundStyle} onThemeSelect={handleThemeApply} />
                            </Tab>
                        </Tabs>
                    </Box>
                </CardBody>
                <CardFooter justify="end" pad={{ top: 'none', left: 'medium', right: 'medium', bottom: 'medium' }}>
                    {values && <Button label={t('deleteButton')} onClick={() => rewardForm.id ? onDelete(rewardForm.id) : {}} busy={deleteBusy} success={deleteSuccess} />}
                    <Button label={t('saveButton')} primary type="submit" busy={submitBusy} success={submitSuccess} />
                </CardFooter>
            </Form>
        </Card>
    )
}

export default RouletteItemSettingsForm;
